import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { sanitizeText } from "@/lib/sanitize";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const INPUT_CLS =
  "h-12 bg-white border-neutral-200 text-[#0A234A] placeholder:text-neutral-400 " +
  "focus-visible:border-[#FF5722] focus-visible:ring-2 focus-visible:ring-[#FF5722]/25 rounded-xl";

const ROLE_FIELDS = {
  driver: [
    { key: "carrier", label: "Carrier / MC or NSC number", placeholder: "e.g. NSC 123456 or MC-987654" },
    { key: "home_base", label: "Home base", placeholder: "e.g. Red Deer, AB" },
    { key: "licence_class", label: "Licence class", placeholder: "e.g. Class 1" },
  ],
  fleet_manager: [
    { key: "company", label: "Company", placeholder: "e.g. Rosenau Transport" },
    { key: "fleet_size", label: "Fleet size", placeholder: "e.g. 45 trucks" },
    { key: "billing_contact", label: "Billing contact", placeholder: "billing@company.com" },
  ],
  fleet_owner: [
    { key: "company", label: "Company", placeholder: "e.g. Rosenau Transport" },
    { key: "fleet_size", label: "Fleet size", placeholder: "e.g. 45 trucks" },
    { key: "billing_contact", label: "Billing contact", placeholder: "billing@company.com" },
  ],
  site_operator: [
    { key: "site_name", label: "Site / hub name", placeholder: "e.g. QEII Mega Hub" },
    { key: "site_location", label: "Location", placeholder: "e.g. Leduc, AB" },
    { key: "capacity", label: "Capacity (bays / pods)", placeholder: "e.g. 60 bays, 24 pods" },
  ],
  gov_agency: [
    { key: "agency", label: "Agency name", placeholder: "e.g. Alberta Transportation" },
    { key: "jurisdiction", label: "Jurisdiction", placeholder: "e.g. Alberta, Canada" },
    { key: "gov_email", label: "Official work email", placeholder: "you@alberta.ca", type: "email" },
  ],
};

const ROLE_COPY = {
  driver: "Tell us about your rig so we can tailor bookings and compliance.",
  fleet_manager: "Set up your fleet so we can track compliance and spend.",
  fleet_owner: "Set up your fleet so we can track compliance and spend.",
  site_operator: "Tell us about your site so we can configure gates and capacity.",
  gov_agency: "Confirm your agency. Government access is reviewed before corridor reporting unlocks.",
};

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "driver";
  const fields = ROLE_FIELDS[role] || ROLE_FIELDS.driver;
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    for (const f of fields) {
      if (!String(form[f.key] || "").trim()) return setError(`Please fill in ${f.label.toLowerCase()}.`);
    }
    const payload = {};
    for (const f of fields) payload[f.key] = f.type === "email" ? String(form[f.key]).trim() : sanitizeText(form[f.key], 200);
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding", payload);
      setUser(data);
      setLoading(false);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setLoading(false);
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="onboarding-page" className="relative min-h-[100dvh] bg-[#F7F8FA] text-[#0A234A]">
      <div aria-hidden className="absolute bottom-0 left-0 z-20 h-2.5 w-full bg-[#FF5722]" />
      <div className="mx-auto flex min-h-[100dvh] max-w-2xl items-center justify-center px-5 py-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full rounded-3xl bg-white p-8 sm:p-10 shadow-[0_24px_60px_-20px_rgba(10,35,74,0.28),0_0_0_1px_rgba(10,35,74,0.05)]"
        >
          <img src="/fleetpods_logo.png" alt="FleetPods" className="mb-6 h-8 w-auto object-contain" />
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#FF5722]">Welcome aboard</p>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-[#0A234A]">Let's set up your account</h1>
          <p className="mt-2 text-neutral-500">{ROLE_COPY[role]}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {fields.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label htmlFor={f.key} className="text-neutral-600">{f.label}</Label>
                <Input
                  id={f.key}
                  data-testid={`onboarding-${f.key}`}
                  type={f.type || "text"}
                  value={form[f.key] || ""}
                  onChange={upd(f.key)}
                  placeholder={f.placeholder}
                  className={INPUT_CLS}
                  maxLength={200}
                />
              </div>
            ))}

            {role === "gov_agency" && (
              <div className="flex items-start gap-2 rounded-xl bg-[#FF5722]/5 p-3 text-sm text-neutral-600">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF5722]" />
                <span>Work emails on a recognised government domain verify instantly. Others are flagged for administrator review.</span>
              </div>
            )}

            {error && (
              <p data-testid="onboarding-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">{error}</p>
            )}

            <Button
              type="submit"
              data-testid="onboarding-submit-btn"
              disabled={loading}
              className="group h-12 w-full rounded-2xl bg-[#FF5722] text-white font-bold hover:bg-[#F4511E] shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.35),inset_0_-4px_6px_rgba(160,40,0,0.35)] transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                <span className="flex items-center gap-2">Continue to dashboard <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
